import React, { createContext, useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useRouteMatch } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import { Segment } from '~/Helpers/Segment.helper';
import {
  requestProviderById,
  selectCurrentProvider,
  selectCurrentProviderError,
  selectCurrentProviderLoading,
} from '~/store/provider';
import { Route } from '~/Constants/Routes.constants';
import { ProviderAccessType } from '~/Models/DTO/Provider.model';
import { useLocalStorage } from '../useLocalStorage';
import { useRequestAvailableProviders } from '~/requests';

interface RouteParams {
  domain?: string;
}

export interface ProviderContextProviderProps {
  children: React.ReactNode;
}

export type ProviderContextType = ReturnType<typeof useProviderWithoutContext>;

const ProviderContext = createContext<ProviderContextType>({} as ProviderContextType);

export const useProviderWithoutContext = () => {
  // hooks
  const dispatch = useDispatch();
  const loc = useLocation();
  const {
    providerUUId,
    setProviderUUId,
    redirectUrl,
    providerUUIdAfterARedirectIsCleaned,
  } = useLocalStorage();

  const {
    data: availableProviders,
    isFetched: hasAvailableProvidersLoaded,
    error: availableProvidersError,
    isLoading: isAvailableProvidersLoading,
  } = useRequestAvailableProviders();

  const matchProvider = useRouteMatch<RouteParams>({ path: Route.PROVIDER_DETAIL, location: loc });
  const matchOrg = loc.pathname.startsWith(Route.ORGANISATION_ROOT);
  // selectors
  const currentProvider = useSelector(selectCurrentProvider);
  const currentProviderLoading = useSelector(selectCurrentProviderLoading);
  const providerLoadError = useSelector(selectCurrentProviderError);

  // state
  const [hasCurrentProviderBeenRequested, setHasCurrentProvideBeenRequested] = useState(false);

  // actions
  const getCurrentProvider = bindActionCreators(requestProviderById, dispatch);

  const currentDomain = matchProvider?.params?.domain;

  useEffect(() => {
    if (currentProviderLoading) {
      setHasCurrentProvideBeenRequested(true);
    }
  }, [currentProviderLoading]);

  useEffect(() => {
    const domainChanged =
      currentProvider?.domain !== currentDomain &&
      currentProvider?.uuid !== currentDomain &&
      !matchOrg;

    if ((!currentProvider || domainChanged) && !providerLoadError && !currentProviderLoading) {
      const availableDomain = availableProviders?.length === 1 && availableProviders?.[0]?.domain;
      const domain = currentDomain || availableDomain || providerUUId;
      if (domain && domain !== 'select') {
        setHasCurrentProvideBeenRequested(false);
        getCurrentProvider(domain);
      }
    }
  }, [currentDomain, availableProviders]);

  useEffect(() => {
    if (currentProvider) {
      Segment.group(currentProvider.uuid);
      // if we have a redirectUrl, we want to make sure we don't set the providerUUId in local storage
      if (!redirectUrl || (redirectUrl && !providerUUIdAfterARedirectIsCleaned)) {
        setProviderUUId(currentProvider.uuid);
      }
      if (currentProvider.accessType === ProviderAccessType.PARTNER_TOOL) {
        Segment.track('PartnerLogin', {
          email: currentProvider.email,
          domain: currentProvider.domain,
        });
      }
    }
  }, [currentProvider]);

  return {
    currentProvider,
    isCurrentProviderLoading:
      currentProviderLoading ||
      isAvailableProvidersLoading ||
      (availableProviders?.length === 1 && !currentProvider),
    isUsingThePartnerTool: currentProvider?.accessType === ProviderAccessType.PARTNER_TOOL,
    availableProviders: availableProviders || [],
    hasAvailableProvidersLoaded,
    getCurrentProvider,
    hasCurrentProviderLoaded:
      (hasCurrentProviderBeenRequested || !!currentProvider) && !currentProviderLoading,
    error: providerLoadError || availableProvidersError,
  };
};

const ProviderContextWrapper = (props: ProviderContextProviderProps) => {
  const store = useProviderWithoutContext();
  const { children } = props;

  return <ProviderContext.Provider value={store}>{children}</ProviderContext.Provider>;
};

const useProvider = () => useContext(ProviderContext);

export { ProviderContextWrapper, useProvider };
